import { z } from "zod";

import { OutputType } from "../../enums.js";
import { AppSessionCellId, OutputId } from "../../idTypeBrands.js";
import { createAppSessionEventDefinition } from "../AppSessionEventDefinition.js";
import { OutputContent } from "../AppSessionEventState.js";

import { zUnattachedOutputContent } from "./zUnattachedOutputContent.js";

export const CREATE_OUTPUT = createAppSessionEventDefinition({
  type: "CREATE_OUTPUT",
  payloadSchema: z.object({
    outputId: OutputId,
    appSessionCellId: AppSessionCellId,
    order: z.number(),
    outputType: z.nativeEnum(OutputType),
    requestId: z.string().nullable(),
    outputContents: z.array(zUnattachedOutputContent),
  }),
  logSafe: {
    outputId: true,
    appSessionCellId: true,
    order: true,
    outputType: true,
    requestId: true,
    outputContents: false,
  },
  create: (payload: {
    outputId: OutputId;
    appSessionCellId: AppSessionCellId;
    order: number;
    outputType: OutputType;
    requestId: string | null;
    outputContents: Omit<OutputContent, "outputId">[];
  }) => payload,
  apply: ({
    indices,
    payload: {
      appSessionCellId,
      order,
      outputContents,
      outputId,
      outputType,
      requestId,
    },
    state,
  }) => {
    const warnings: string[] = [];

    if (state.appSessionCells[appSessionCellId] == null) {
      warnings.push(`App session cell not found: ${appSessionCellId}`);
    }

    if (state.outputs[outputId] != null) {
      warnings.push(`Output previously existed, overwriting: ${outputId}`);
    }

    state.outputs[outputId] = {
      id: outputId,
      appSessionCellId,
      order,
      outputType,
      requestId,
    };

    for (const outputContent of outputContents) {
      if (state.outputContents[outputContent.id] != null) {
        warnings.push(
          `Output content previously existed, overwriting: ${outputContent.id}`,
        );
      }

      state.outputContents[outputContent.id] = {
        ...outputContent,
        outputId,
      };
    }

    (indices.appSessionCellIdToOutputIds[appSessionCellId] ??= []).push(
      outputId,
    );
    indices.outputIdToOutputContentIds[outputId] = outputContents.map(
      (c) => c.id,
    );

    return { warnings };
  },
});
